import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAnalyticsContext } from 'AnalyticsDashboard/AnalyticsContext'
import restClient from 'Shared/hooks/restClient'
import { CHART_TYPES } from './QueryBuilder/chartTypes'
import { buildConfig, restoreSlots } from './QueryBuilder/buildConfig'
import { SlotEditor } from './QueryBuilder/SlotEditor'
import { Filters } from './Filters'
import RawSqlEditor from './QueryBuilder/RawSqlEditor'
import type { Metric, FactTable, Dimension, RawSqlColumn, RawSqlFactTablePayload } from 'AnalyticsDashboard/utils/factTableHelpers'
import {
  referencedFactTableKinds,
  computeAvailableDimensions,
  computeAvailableFilters,
  metricDisplayLabel,
  rawSqlFactTableToFactTable,
} from 'AnalyticsDashboard/utils/factTableHelpers'

declare const _: any

interface QueryBuilderProps {
  onSubmit: (config: Record<string, any>) => void
  onCancel: () => void
  initialConfig?: Record<string, any> | null
}

function generateName(
  chartType: string,
  slotValues: Record<string, any>,
  factTables: FactTable[],
  queryInfo: { availableDimensions: Dimension[] } | null
): string {
  if (!chartType) return ''
  const chartDef = CHART_TYPES[chartType]
  if (!chartDef) return ''

  const parts: string[] = []

  // Derive data source name from metrics' fact tables
  const metrics: Metric[] = Array.isArray(slotValues.metrics) ? slotValues.metrics : []
  const refKinds = referencedFactTableKinds(metrics)
  if (refKinds.length > 0) {
    const ftLabels = refKinds
      .map(kind => factTables.find(ft => ft.kind === kind)?.label || _.capitalize(kind))
      .slice(0, 2)
    parts.push(ftLabels.join(' + '))
  }

  // Metric labels
  if (metrics.length > 0) {
    const labels = metrics
      .slice(0, 3)
      .map(m => metricDisplayLabel(m, factTables))
    parts.push(labels.join(', '))
  }

  // Primary dimension
  if (queryInfo) {
    for (const [key, slot] of Object.entries(chartDef.slots)) {
      if (slot.kind === 'dimension' || slot.kind === 'time') {
        const val = slotValues[key]
        const dimId = val?.id
        if (dimId) {
          const dimInfo = queryInfo.availableDimensions?.find(d => d.id === dimId)
          parts.push('by ' + (dimInfo?.name || dimInfo?.label || dimId))
          break
        }
      }
    }
  }

  return parts.join(' — ')
}

export default function QueryBuilder({ onSubmit, onCancel, initialConfig = null }: QueryBuilderProps) {
  const { state: { queriesInfo: factTables, global: isGlobal } } = useAnalyticsContext()

  const [chartType, setChartType] = useState(initialConfig?.chartType || '')
  const [slotValues, setSlotValues] = useState<Record<string, any>>(() => restoreSlots(initialConfig))
  const [overrideFilters, setOverrideFilters] = useState<Record<string, any>>(initialConfig?.overrideFilters || {})
  const [orderByMetric, setOrderByMetric] = useState(initialConfig?.orderBy?.[0]?.id || '')
  const [orderByDirection, setOrderByDirection] = useState(initialConfig?.orderBy?.[0]?.direction || 'desc')
  const [limit, setLimit] = useState(initialConfig?.limit || '')
  const [name, setName] = useState(initialConfig?.name || '')
  const [assistantConversationId, setAssistantConversationId] = useState<number | undefined>(initialConfig?.assistantConversationId)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)

  const handleAiGenerate = useCallback(async () => {
    if (!aiPrompt.trim()) return
    setAiLoading(true)
    setAiError(null)
    try {
      const { data: aiConfig } = await restClient.post(
        '/api/analytics/analytics_queries/generate_chart',
        { prompt: aiPrompt, conversationId: assistantConversationId }
      )

      // When the LLM produced a raw_sql_fact_table, synthesize a client-side
      // FactTable so the standard dimension/filter pipeline finds it.
      const effectiveFactTables = aiConfig.rawSqlFactTable
        ? [...factTables, rawSqlFactTableToFactTable(aiConfig.rawSqlFactTable, factTables)]
        : factTables

      const returnedConversationId = aiConfig.assistantConversationId
      setAssistantConversationId(returnedConversationId)
      setAiPrompt('')

      const refKinds = referencedFactTableKinds(aiConfig.metrics || [])
      const availableDims = computeAvailableDimensions(effectiveFactTables, refKinds)
      const slots = restoreSlots(aiConfig)
      submitConfig(aiConfig.chartType, slots, { availableDimensions: availableDims }, {
        name: aiConfig.name,
        orderBy: aiConfig.orderBy || [],
        limit: aiConfig.limit ?? null,
        rawSqlFactTable: aiConfig.rawSqlFactTable,
        assistantConversationId: returnedConversationId,
      }, effectiveFactTables)
    } catch (err: any) {
      setAiError(err.response?.data?.error || 'Failed to generate chart')
    } finally {
      setAiLoading(false)
    }
  }, [aiPrompt, assistantConversationId, onSubmit, factTables])

  const chartDef = CHART_TYPES[chartType]
  const isEditing = !!initialConfig

  // The raw_sql_fact_table is mutable in this session — either edited via
  // RawSqlEditor on an existing AI-generated tile (Flow 1) or hand-written from
  // scratch (Flow 2). The synthetic fact table folds into the catalogue so the
  // slot UI's dimension/filter pickers find it under kind 'raw_sql'.
  const [rawSqlFactTable, setRawSqlFactTable] = useState<RawSqlFactTablePayload | undefined>(initialConfig?.rawSqlFactTable)
  const initialSql = initialConfig?.rawSqlFactTable?.sql
  // True when the user clicked "Write custom SQL" to start a brand-new tile;
  // keeps the editor visible until they've validated something. The editor
  // also stays visible as long as `rawSqlFactTable` is present.
  const [manualSqlMode, setManualSqlMode] = useState(false)
  // True when the editor's current SQL has been edited past the last successful
  // validate. Save is gated on this being false.
  const [sqlDirty, setSqlDirty] = useState(false)
  // Backend-synthesised fact_tables_info-shape for the current raw_sql payload.
  // Set when validate returns; on open with an existing rawSqlFactTable, the
  // useEffect below fires its own validate to populate it.
  const [rawSqlFactTableMeta, setRawSqlFactTableMeta] = useState<FactTable | null>(null)

  useEffect(() => {
    if (!rawSqlFactTable) {
      setRawSqlFactTableMeta(null)
      return
    }
    let cancelled = false
    restClient
      .post('/api/analytics/analytics_queries/validate_raw_sql', { sql: rawSqlFactTable.sql })
      .then(({ data }) => {
        if (cancelled || !data?.valid || !data.factTable) return
        setRawSqlFactTableMeta(data.factTable)
      })
      .catch(() => { /* fall back to client-side synthesis */ })
    return () => { cancelled = true }
  }, [rawSqlFactTable?.sql])

  const factTablesWithRawSql: FactTable[] = useMemo(() => {
    if (!rawSqlFactTable) return factTables
    const synth = rawSqlFactTableMeta || rawSqlFactTableToFactTable(rawSqlFactTable, factTables)
    return [...factTables, synth]
  }, [factTables, rawSqlFactTable, rawSqlFactTableMeta])

  const handleRawSqlValidated = useCallback((sql: string, columns: RawSqlColumn[], factTable: FactTable | null) => {
    setRawSqlFactTable({ sql, columns })
    setRawSqlFactTableMeta(factTable)
    setSqlDirty(false)
    setManualSqlMode(false)
  }, [])

  const handleRawSqlInvalidate = useCallback(() => {
    setSqlDirty(true)
  }, [])

  // Compute queryInfo dynamically from selected metrics' fact tables
  const selectedMetrics: Metric[] = useMemo(() => {
    const raw = slotValues.metrics
    return Array.isArray(raw) ? raw : raw ? [raw] : []
  }, [slotValues.metrics])

  const queryInfo = useMemo(() => {
    const refKinds = referencedFactTableKinds(selectedMetrics)
    const singleClient = !isGlobal
    return {
      availableDimensions: computeAvailableDimensions(factTablesWithRawSql, refKinds),
      availableFilters: computeAvailableFilters(factTablesWithRawSql, refKinds, singleClient),
    }
  }, [factTablesWithRawSql, selectedMetrics, isGlobal])

  const handleChartTypeChange = (newType: string) => {
    setChartType(newType)
    setSlotValues(newType ? CHART_TYPES[newType].defaults() : {})
    setOrderByMetric('')
  }

  const setSlot = (slotKey: string, value: any) => {
    setSlotValues(prev => {
      const next = { ...prev, [slotKey]: value }

      // If a dimension slot changed, clear other dimension slots that now conflict
      if (chartDef && value?.id) {
        for (const [otherKey, otherSlot] of Object.entries(chartDef.slots)) {
          if (otherKey === slotKey || otherSlot.kind !== 'dimension') continue
          const otherVal = next[otherKey]
          if (otherSlot.single && otherVal?.id === value.id) {
            next[otherKey] = null
          } else if (Array.isArray(otherVal)) {
            next[otherKey] = otherVal.filter((d: any) => (d.id || d) !== value.id)
          }
        }
      }

      return next
    })
  }

  const autoName = useMemo(
    () => generateName(chartType, slotValues, factTables, queryInfo),
    [chartType, slotValues, factTables, queryInfo]
  )

  const isStatic = chartDef?.static

  const submitConfig = (configChartType: string, configSlots: Record<string, any>, configQueryInfo: { availableDimensions: Dimension[] }, overrides: Record<string, any> = {}, overrideFactTables?: FactTable[]) => {
    const configChartDef = CHART_TYPES[configChartType]
    if (!configChartDef) return

    if (configChartDef.static) {
      onSubmit({ name: overrides.name || autoName, chartType: configChartType, chartOptions: configChartDef.buildChartOptions(configSlots, []), static: true })
      return
    }

    const effectiveFactTables = overrideFactTables || factTables
    const { dimensions, metrics, chartOptions } = buildConfig(configChartType, configSlots, configQueryInfo, effectiveFactTables)
    const primaryDimCol = chartOptions.columns?.[0]
    const config: Record<string, any> = {
      name: overrides.name || autoName,
      chartType: configChartType,
      metrics,
      dimensions,
      primaryDimension: primaryDimCol
        ? { id: primaryDimCol.id, label: primaryDimCol.label }
        : { id: 'time', label: 'Time' },
      chartOptions,
      orderBy: overrides.orderBy || [],
      limit: overrides.limit ?? null,
      overrideFilters: overrides.overrideFilters || {},
      ...(configSlots.comparePrevious ? { comparePeriods: 1 } : {}),
    }

    if (overrides.rawSqlFactTable) config.rawSqlFactTable = overrides.rawSqlFactTable
    if (overrides.assistantConversationId) config.assistantConversationId = overrides.assistantConversationId

    if (dimensions.length > 1) {
      const secDimCol = chartOptions.columns?.find((c: any) => c.id === _.camelCase(dimensions[1].id))
      if (secDimCol) config.secondaryDimension = { id: secDimCol.id, label: secDimCol.label }
    }

    onSubmit(config)
  }

  const handleSubmit = async () => {
    let conversationId = assistantConversationId

    // If the raw SQL has been hand-edited (or hand-written) since this session
    // began, log it onto the assistant conversation so Regenerate sees the
    // current SQL as prior context. For Flow 2 (no conversation yet) the
    // endpoint creates one and returns the new id.
    const sqlChanged = rawSqlFactTable && rawSqlFactTable.sql !== initialSql
    if (sqlChanged) {
      try {
        const { data } = await restClient.post(
          '/api/analytics/analytics_queries/log_raw_sql_edit',
          { conversationId, sql: rawSqlFactTable!.sql }
        )
        if (data.assistantConversationId) conversationId = data.assistantConversationId
      } catch (_e) {
        // Best-effort — don't block Save on a logging hiccup.
      }
    }

    submitConfig(chartType, slotValues, queryInfo, {
      name: name || autoName,
      orderBy: orderByMetric ? [{ kind: 'metric', id: orderByMetric, direction: orderByDirection }] : [],
      limit: limit ? parseInt(limit, 10) : null,
      overrideFilters: _.pickBy(overrideFilters, (v: any) => v !== undefined && v !== null),
      rawSqlFactTable: rawSqlFactTable,
      assistantConversationId: conversationId,
    }, factTablesWithRawSql)
  }

  const slotsValid = chartDef && Object.entries(chartDef.slots).every(([key, slot]) => {
    if (!slot.required) return true
    const val = slotValues[key]
    if (Array.isArray(val)) return val.length > 0
    return val !== null && val !== undefined && val !== ''
  })

  const hasMetrics = selectedMetrics.length > 0
  const isValid = chartType && chartDef && (isStatic || hasMetrics) && slotsValid && !sqlDirty

  // During manual SQL construction we treat it as a 3-step wizard:
  //   1. Write + validate SQL
  //   2. Pick a chart type
  //   3. Configure metrics/dimensions and save
  // Hide unrelated UI between steps so the next step is obvious.
  const writingManualSql = manualSqlMode && !rawSqlFactTable
  const pickingChartTypeForManualSql = !!rawSqlFactTable && !isEditing && !chartType && !manualSqlMode

  return (
    <div className="query-builder">
      {/* Step 1 (manual SQL flow): write + validate the SQL. Renders first so
          it's the natural focus point; in the AI/edit flows it sits below the
          chart picker but still above the slot UI. */}
      {(rawSqlFactTable || manualSqlMode) && (
        <RawSqlEditor
          initialSql={rawSqlFactTable?.sql || ''}
          onValidated={handleRawSqlValidated}
          onInvalidate={handleRawSqlInvalidate}
        />
      )}

      {pickingChartTypeForManualSql && (
        <div className="query-builder__step-hint">
          \u2713 SQL validated. Pick a chart type below to continue.
        </div>
      )}

      {!isEditing && !chartType && !writingManualSql && (
        <div className="query-builder__section">
          <h3>{pickingChartTypeForManualSql ? 'Step 2: Choose a chart type' : 'Visualisation'}</h3>
          <div className="query-builder__options">
            {Object.entries(CHART_TYPES).map(([id, def]) => (
              <button
                key={id}
                type="button"
                className={`query-builder__option ${chartType === id ? 'active' : ''}`}
                onClick={() => handleChartTypeChange(id)}
              >
                <span>{def.label}</span>
                <span className="query-builder__option-desc">{def.description}</span>
              </button>
            ))}
            {!rawSqlFactTable && (
              <button
                type="button"
                className="query-builder__option"
                onClick={() => setManualSqlMode(true)}
              >
                <span>Write custom SQL</span>
                <span className="query-builder__option-desc">Hand-write the SELECT, columns auto-detected.</span>
              </button>
            )}
          </div>
        </div>
      )}

      {!writingManualSql && !pickingChartTypeForManualSql && (
        <div className="query-builder__ai-section">
          <h4>{isEditing || chartType ? 'Edit chart with AI' : 'Make a chart using AI'}</h4>
          <div className="query-builder__ai-row">
            <textarea
              className="query-builder__ai-input"
              placeholder="Email open rate by week"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiGenerate() } }}
              disabled={aiLoading}
              rows={2}
            />
            <button
              type="button"
              className="query-builder__ai-button"
              onClick={handleAiGenerate}
              disabled={aiLoading || !aiPrompt.trim()}
            >
              {aiLoading ? 'Generating...' : (isEditing || chartType ? 'Regenerate' : 'Generate')}
            </button>
          </div>
          {aiError && <div className="query-builder__ai-error">{aiError}</div>}
        </div>
      )}

      {chartType && (
        <div className="query-builder__change-chart">
          {CHART_TYPES[chartType]?.label}
          {!isEditing && (
            <>
              {' \u2014 '}
              <a href="#" onClick={(e) => { e.preventDefault(); handleChartTypeChange('') }}>Change chart</a>
            </>
          )}
        </div>
      )}

      {chartDef && (
        <>
          {Object.entries(chartDef.slots).map(([slotKey, slot]) => {
            // Only show metric slots when no metrics are selected yet
            if (!isStatic && !hasMetrics && slot.kind !== 'metric') return null
            return (
              <SlotEditor
                key={slotKey}
                slot={slot}
                slotKey={slotKey}
                value={slotValues[slotKey]}
                allSlotValues={slotValues}
                allSlots={chartDef.slots}
                onChange={(val) => setSlot(slotKey, val)}
                queryInfo={isStatic ? null : queryInfo}
                factTables={factTablesWithRawSql}
              />
            )
          })}

          {!isStatic && hasMetrics && queryInfo.availableFilters.length > 0 && (
            <div className="query-builder__section">
              <h3>Filters <span className="query-builder__optional">(optional)</span></h3>
              <Filters
                filters={queryInfo.availableFilters}
                filterValues={overrideFilters}
                setFilterValue={(id: string, value: any) => setOverrideFilters(prev => ({ ...prev, [id]: value }))}
                clearFilterValue={(id: string) => setOverrideFilters(prev => { const next = { ...prev }; delete next[id]; return next })}
              />
            </div>
          )}

          {hasMetrics && chartDef.supportsOrderLimit && (
            <div className="query-builder__section">
              <h3>Order & Limit <span className="query-builder__optional">(optional)</span></h3>
              <div className="query-builder__row">
                <select value={orderByMetric} onChange={(e) => setOrderByMetric(e.target.value)}>
                  <option value="">No ordering</option>
                  {selectedMetrics.map(m => (
                    <option key={m.id} value={m.id}>{metricDisplayLabel(m, factTables)}</option>
                  ))}
                </select>
                {orderByMetric && (
                  <select value={orderByDirection} onChange={(e) => setOrderByDirection(e.target.value)}>
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                )}
                <input
                  type="number"
                  placeholder="Limit (e.g. 10)"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  min="1"
                  style={{ width: 140 }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {chartDef && (
        <div className="query-builder__section">
          <h3>Name</h3>
          <input
            type="text"
            className="query-builder__name-input"
            placeholder={autoName || 'Query name'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      )}

      <div className="query-builder__actions">
        <button type="button" className="query-builder__cancel" onClick={onCancel}>Cancel</button>
        <button type="button" className="query-builder__submit" onClick={handleSubmit} disabled={!isValid}>
          {initialConfig ? 'Update' : 'Add'}
        </button>
      </div>
    </div>
  )
}
